// Core packages

// Third party packages
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";

// Custom packages
import {
  autoRenewalFromDuration,
  capabilitiesOf,
  DATE_NOT_AVAILABLE_LABEL_KEY,
  DATE_NOT_AVAILABLE_SUFFIX,
  FIELD_LABEL_PREFIX,
  isDataTypeRequiredWhenMet,
} from "src/app/modules/settings/data-types.utils";
import {
  DataTypeField,
  DataTypeFieldOptionsSource,
  DataTypeRequiredWhen,
} from "src/app/shared/interfaces/data-type.interface";

/**
 * The `SelectNew` providers whose dropdown also takes a name that is not listed yet: the tenants
 * (`conductorName`), the archiving interventions (`nameInterventionArchiving`), the supplier names
 * (`suppliersName`) and the years of storage (`yearsOfStorage`) of the property's archive — every
 * provider, as on the legacy archive, where every `SelectNew` takes one. Typing it is the only way
 * to file the first name of a list that is still empty, the first tenant of a property above all.
 *
 * The supplier names and the years were closed until the decision of 27 October 2026 (FE-M6): the
 * two fields stay, the user fills them in and edits them, and their lists come from the archive as
 * soon as the back-end serves them.
 *
 * @since 3.0.0
 */
const NEW_NAME_SOURCES: DataTypeFieldOptionsSource[] = [
  "conductorName",
  "nameInterventionArchiving",
  "suppliersName",
  "yearsOfStorage",
];

/**
 * How a name typed into one of those dropdowns is stored: without dots, without spaces at the two
 * ends, and upper-cased, so «Rossi M.» and « Rossi M. » both become «ROSSI M». The dots go first,
 * so «Rossi M .» loses its last space too; the spaces between the words stay.
 *
 * Dots and casing are the legacy archive's rule (`documentTypes.ts`, `SelectNew`). It matters for
 * the tenant above all: the tree groups the rows by the value of the field, case included, and the
 * back-end lists the name upper-cased, so a name stored as typed would open a second group the next
 * time it is picked from the list. The supplier name groups its section the same way. The
 * intervention and the year follow the same rule.
 *
 * The ends are trimmed because the back-end stores the value as sent but lists it trimmed, while
 * the rename and the removal of the two name windows match the stored value spaces included: a name
 * kept with a trailing space would be left behind by a rename, and would not stop a removal.
 *
 * @since 3.0.0
 */
const toListedName = (term: string): string => term.replace(/\./g, "").trim().toUpperCase();

/**
 * What the caller can supply that only exists inside a property.
 *
 * Empty (the default) is the SETTINGS PREVIEW: the widgets that depend on a property render
 * empty on purpose, because a settings page has no property. Filled in, the very same builder
 * produces the form the ARCHIVE uses — which is the point of having one builder.
 *
 * @since 3.0.0
 */
export interface DataTypeFormContext {
  /**
   * Remote search of the supplier autocomplete, i.e. `SuppliersService.getListV2`.
   *
   * Supplying it also switches the field to the DEDICATED `autocomplete-supplier` widget: that
   * component is the one that writes the chosen company name into the hidden companion field
   * (`ragioneSocialeFornitore`), so the generic `autocomplete` cannot stand in for it outside a
   * preview.
   */
  supplierSearch?: any;
  /**
   * Resolved values of the `SelectNew` providers, keyed by `optionsSource`
   * (`conductorName`, `nameInterventionArchiving`, `suppliersName`, `yearsOfStorage`).
   *
   * A provider with no entry renders an empty list, exactly as the preview does.
   */
  optionLists?: { [source: string]: string[] };
  /**
   * Force the whole form to be non interactive: the "view additional data" mode of the archive.
   *
   * It also strips the `expressionProperties`, so nothing can flip a field back to enabled or
   * required while the user is only reading.
   */
  readonlyAll?: boolean;
}

/**
 * Renders a data type into the Formly form the user will actually face when archiving
 * a document, so that the editor shows a form being designed instead of a table of
 * metadata being filled in.
 *
 * It is a faithful port of `documentTypes.ts:getFormlyFields2()` plus the per-page post
 * processing every archive page repeats (translating labels, resolving the option
 * lists): that function cannot be reused as is because it reads the HARDCODED
 * dictionary by schema name and consumes the legacy per-field object shape, while this
 * one takes the `fields[]` array of the new API.
 *
 * Two deliberate differences, both about data that only exists inside a property:
 *  - `SelectNew` renders its widget with an EMPTY list: its provider (conductors of the
 *    property, suppliers, ...) has no meaning in a settings page;
 *  - `Autocomplete-Supplier` renders the same way, with no remote search wired.
 * Everything that can be verified without a property - labels, required, readonly, the
 * `requiredWhen` and `onChange` rules - behaves exactly like in the archive.
 *
 * `requiredWhen` and `onChange` have NO generic runtime in the archive yet: today they
 * live as hand written closures inside the dictionary. This is their first declarative
 * implementation, and it is the reference the archive can adopt at cutover.
 *
 * @since 3.0.0
 */
export class DataTypePreviewBuilder {
  /**
   * The property scoped data of the build in progress.
   *
   * Held on the instance rather than threaded through every private method: `build` is
   * synchronous and not reentrant, so it is set at the start of a build and read during it.
   */
  private context: DataTypeFormContext = {};

  /**
   * Class constructor
   */
  constructor(private translateService: TranslateService) {}

  /**
   * Build the preview of a whole data type.
   *
   * @since 3.0.0
   *
   * @param fields    Fields in the order they will be sent (position is not re-read here)
   * @param [context] Property scoped data. Omitted = the settings preview.
   *                  @see DataTypeFormContext
   */
  build(
    fields: DataTypeField[],
    context?: DataTypeFormContext
  ): FormlyFieldConfig[] {
    this.context = context ? context : {};

    const configs: FormlyFieldConfig[] = [];
    (Array.isArray(fields) ? fields : []).forEach((field) => {
      if (!field || !field.name || !field.type) {
        return;
      }
      this.buildField(field).forEach((config) => configs.push(config));
    });

    if (this.context.readonlyAll) {
      configs.forEach((config) => this.freeze(config));
    }

    return [
      {
        fieldGroupClassName: "row",
        fieldGroup: configs,
      },
    ];
  }

  /**
   * Make a field, and anything nested under it, non interactive.
   *
   * The `expressionProperties` are DROPPED and not merely overridden: they exist to flip
   * `required` and `disabled` while the user types, and left in place they would undo the
   * freeze on the first change detection pass.
   *
   * `required` is deliberately LEFT ALONE. It is what draws the asterisk next to the label — both
   * in the Material widgets and in the custom autocomplete, which renders its own — and a read
   * only form still has to show which fields are compulsory, exactly like the legacy dialog does.
   * Validation is not a concern: a disabled control is excluded from it, and this form is never
   * submitted.
   *
   * @since 3.0.0
   */
  private freeze(config: FormlyFieldConfig): void {
    if (!config) {
      return;
    }
    delete config.expressionProperties;
    if (config.templateOptions) {
      config.templateOptions.readonly = true;
      config.templateOptions.disabled = true;
    }
    if (Array.isArray(config.fieldGroup)) {
      config.fieldGroup.forEach((child) => this.freeze(child));
    }
  }

  /**
   * Build the one (or two, for DateNotAvailable) Formly configs of a single field.
   *
   * @since 3.0.0
   */
  private buildField(field: DataTypeField): FormlyFieldConfig[] {
    switch (field.type) {
      case "String":
        return [this.buildString(field)];
      case "Textarea":
        return [this.buildTextarea(field)];
      case "Number":
        return [this.buildNumber(field)];
      case "Date":
        return [this.buildDate(field)];
      case "DateNotAvailable":
        return this.buildDateNotAvailable(field);
      case "Select":
        return [this.buildSelect(field)];
      case "SelectNew":
        return [this.buildSelectNew(field)];
      case "Autocomplete-Supplier":
        return [this.buildSupplierAutocomplete(field)];
      case "Hidden":
        return [this.buildHidden(field)];
      default:
        return [];
    }
  }

  /**
   * Text input.
   *
   * @since 3.0.0
   */
  private buildString(field: DataTypeField): FormlyFieldConfig {
    return {
      className: "col-12",
      type: "input",
      key: field.name,
      defaultValue: this.defaultValueOf(field),
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        label: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
        minLength: field.minLength,
        maxLength: field.maxLength,
      },
    };
  }

  /**
   * Multi line text box.
   *
   * `rows` is the height, and it is passed through as it comes: the back-end accepts it only on
   * this type, so a value that arrives here is always applicable. Omitted, Formly falls back on
   * the default height of the widget.
   *
   * It is the type of the two common fields of every document form, `contenutoDocumento` and
   * `keyword`, which used to be a hardcoded literal repeated in fourteen copies across the pages.
   *
   * @since 3.0.0
   */
  private buildTextarea(field: DataTypeField): FormlyFieldConfig {
    return {
      className: "col-12",
      type: "textarea",
      key: field.name,
      defaultValue: this.defaultValueOf(field),
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        label: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
        minLength: field.minLength,
        maxLength: field.maxLength,
        rows: field.rows,
      },
    };
  }

  /**
   * Numeric input. The archive forces a floor of 0 and a ceiling of 99999 when the
   * field declares none: reproduced here, or the preview would accept values the
   * archive refuses.
   *
   * @since 3.0.0
   */
  private buildNumber(field: DataTypeField): FormlyFieldConfig {
    const min = typeof field.min === "number" ? field.min : 0;
    return {
      className: "col-12",
      type: "input",
      key: field.name,
      defaultValue:
        typeof field.defaultValue !== "undefined" && field.defaultValue !== ""
          ? field.defaultValue
          : min,
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        label: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
        type: "number",
        step: 1,
        min,
        max: typeof field.max === "number" ? field.max : 99999,
      },
    };
  }

  /**
   * Date picker.
   *
   * @since 3.0.0
   */
  private buildDate(field: DataTypeField): FormlyFieldConfig {
    return {
      className: "col-12",
      type: "datepicker",
      key: field.name,
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        label: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
      },
    };
  }

  /**
   * Date picker paired with a "not available" checkbox: ticking the box disables the
   * date and clears its required state. The pair is what makes `requiredWhen`
   * unavailable on this type, both here and on the back-end.
   *
   * @since 3.0.0
   */
  private buildDateNotAvailable(field: DataTypeField): FormlyFieldConfig[] {
    const flagKey = `${field.name}${DATE_NOT_AVAILABLE_SUFFIX}`;
    return [
      {
        className: "col-8",
        type: "datepicker",
        key: field.name,
        expressionProperties: {
          "templateOptions.required": (model: any): boolean => !model[flagKey],
          "templateOptions.disabled": (model: any): boolean => !!model[flagKey],
        },
        templateOptions: {
          label: this.labelOf(field),
          placeholder: this.labelOf(field),
          required: !!field.required,
          readonly: !!field.readonly,
        },
      },
      {
        className: "col-4",
        type: "checkbox",
        key: flagKey,
        defaultValue: false,
        templateOptions: {
          label: this.instant(DATE_NOT_AVAILABLE_LABEL_KEY),
          required: false,
        },
      },
    ];
  }

  /**
   * Closed list embedded in the data type. Option labels are FULL i18n keys and are
   * resolved here, exactly like the archive pages do before handing the field to
   * Formly.
   *
   * @since 3.0.0
   */
  private buildSelect(field: DataTypeField): FormlyFieldConfig {
    const options = (Array.isArray(field.options) ? field.options : []).map(
      (option) => ({
        value: option.value,
        label: option.label ? this.instant(option.label) : option.value,
      })
    );
    return {
      className: "col-12",
      type: "select",
      key: field.name,
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        label: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
        options,
      },
    };
  }

  /**
   * `SelectNew`: a searchable list whose values come from a provider scoped to the property.
   *
   * The widget is the generic `autocomplete`, which is where the archive ends up too: the legacy
   * emits a placeholder type `selectNew` that every page immediately rewrites to `autocomplete`
   * while resolving the provider — `selectNew` is not a registered Formly type and never reaches
   * Formly.
   *
   * With no list in the context the widget renders empty, which is the settings preview.
   *
   * Every provider's list also takes a name typed by hand (the «Aggiungi nuovo» entry), turned into
   * the form the lists hold (@see NEW_NAME_SOURCES, toListedName). The settings preview offers it as
   * well: nothing is saved there.
   *
   * @since 3.0.0
   */
  private buildSelectNew(field: DataTypeField): FormlyFieldConfig {
    const config = this.buildAutocomplete(field);
    const source = field.optionsSource;
    const lists = this.context.optionLists;
    if (config.templateOptions && source && lists && Array.isArray(lists[source])) {
      config.templateOptions.initialValues = lists[source];
    }
    if (config.templateOptions && source && NEW_NAME_SOURCES.indexOf(source) >= 0) {
      config.templateOptions.addTag = toListedName;
    }
    return config;
  }

  /**
   * `Autocomplete-Supplier`: the supplier picker.
   *
   * With a `supplierSearch` in the context it becomes the DEDICATED `autocomplete-supplier`
   * widget, wired to the remote search — that component is the one that copies the chosen
   * company name into the hidden companion field, so the generic autocomplete is not a
   * substitute for it outside a preview.
   *
   * Without it, it stays the generic widget with no search: a settings page has no supplier
   * registry to query.
   *
   * @since 3.0.0
   */
  private buildSupplierAutocomplete(field: DataTypeField): FormlyFieldConfig {
    const config = this.buildAutocomplete(field);
    if (config.templateOptions && this.context.supplierSearch) {
      config.type = "autocomplete-supplier";
      config.templateOptions.search$ = this.context.supplierSearch;
    }
    return config;
  }

  /**
   * Shared base of the two searchable widgets above.
   *
   * @since 3.0.0
   */
  private buildAutocomplete(field: DataTypeField): FormlyFieldConfig {
    return {
      className: "col-12",
      type: "autocomplete",
      key: field.name,
      expressionProperties: this.buildExpressions(field),
      templateOptions: {
        customLabel: this.labelOf(field),
        placeholder: this.labelOf(field),
        required: !!field.required,
        readonly: !!field.readonly,
        multiple: !!field.multiple,
        bindLabel: field.bindLabel,
        bindValue: field.bindValue,
        initialValues: [],
        addTagText: this.instant("common.words.addTag"),
      },
    };
  }

  /**
   * Hidden field: it carries a value and renders nothing at all, which is exactly what
   * the archive does. A Formly config with no `type` is the archive's own way of
   * expressing it.
   *
   * @since 3.0.0
   */
  private buildHidden(field: DataTypeField): FormlyFieldConfig {
    return {
      className: "col-12",
      key: field.name,
      defaultValue: this.defaultValueOf(field),
    };
  }

  /**
   * Expression properties of a field: the declarative `requiredWhen` and `onChange`
   * rules, compiled into what Formly understands.
   *
   * @since 3.0.0
   */
  private buildExpressions(field: DataTypeField): any {
    const expressions: any = {};

    if (field.requiredWhen && capabilitiesOf(field.type).requiredWhen) {
      const rule = field.requiredWhen;
      expressions["templateOptions.required"] = (model: any): boolean =>
        this.evaluateRequiredWhen(rule, model);
    }

    if (
      field.onChange &&
      field.onChange.handler === "autoRenewalFromDuration" &&
      field.onChange.targetField
    ) {
      expressions["templateOptions.onChange"] = this.buildAutoRenewalRunner(
        field.name,
        field.onChange.targetField,
        !!field.onChange.applyOnInit
      );
    }

    return Object.keys(expressions).length ? expressions : undefined;
  }

  /**
   * Is the rule satisfied by the current model?
   *
   * A value the user has not filled in yet is compared as an empty string, so that
   * `ne`/`nin` do not fire on an untouched form.
   *
   * @since 3.0.0
   */
  private evaluateRequiredWhen(rule: DataTypeRequiredWhen, model: any): boolean {
    return isDataTypeRequiredWhenMet(rule, model);
  }

  /**
   * `autoRenewalFromDuration`: derives the renewal type from the selected duration and
   * writes it into the target field. Empty duration gives an empty target, '0a' gives
   * "Senza rinnovo automatico", anything else "Rinnovo automatico".
   *
   * The written strings are LITERALS, not i18n keys: that is how they are stored in the
   * already archived documents, and changing them here would break the comparison the
   * archive does on saved data.
   *
   * Runs as a side effect of an expression property, like the archive does today. Two
   * guards keep it sane: it only acts when the source value actually changed (unless
   * `applyOnInit` asked for a pass at opening), and it never writes a value the target
   * already holds, which is what would otherwise loop forever.
   *
   * @since 3.0.0
   */
  private buildAutoRenewalRunner(
    sourceName: string,
    targetName: string,
    applyOnInit: boolean
  ): (model: any, formState: any, field: any) => void {
    let initialised = false;
    let lastSource: any;

    return (model: any, formState: any, field: any): void => {
      const current =
        model && typeof model[sourceName] !== "undefined"
          ? model[sourceName]
          : "";
      const isFirstPass = !initialised;
      initialised = true;

      if (isFirstPass && !applyOnInit) {
        lastSource = current;
        return;
      }
      if (!isFirstPass && current === lastSource) {
        return;
      }
      lastSource = current;

      const next = this.autoRenewalValue(current);
      if (typeof next === "undefined") {
        return;
      }
      const control =
        field && field.form && field.form.controls
          ? field.form.controls[targetName]
          : undefined;
      if (control && control.value !== next) {
        control.setValue(next);
      }
    };
  }

  /**
   * Value `autoRenewalFromDuration` derives from a duration. `undefined` means "leave
   * the target alone".
   *
   * @since 3.0.0
   */
  private autoRenewalValue(duration: any): string {
    return autoRenewalFromDuration(duration);
  }

  /**
   * Default value of a field, preserving the meaningful empty string.
   *
   * @since 3.0.0
   */
  private defaultValueOf(field: DataTypeField): any {
    return typeof field.defaultValue !== "undefined"
      ? field.defaultValue
      : undefined;
  }

  /**
   * Label the archive shows for a field.
   *
   * Reproduces the three behaviours of the archive renderer, warts included: some
   * types ignore the stored label and use the field name, and the two date types have
   * NO fallback, so a missing label surfaces as the raw unresolved key. Hiding that
   * here would make the preview lie.
   *
   * @since 3.0.0
   */
  private labelOf(field: DataTypeField): string {
    const usage = capabilitiesOf(field.type).labelUsage;
    if (usage === "ignored") {
      return this.instant(FIELD_LABEL_PREFIX + field.name);
    }
    if (usage === "required") {
      return this.instant(FIELD_LABEL_PREFIX + field.label);
    }
    return this.instant(
      FIELD_LABEL_PREFIX + (field.label ? field.label : field.name)
    );
  }

  /**
   * Shortcut on the translate service. A missing key resolves to the key itself, which
   * is precisely what the archive shows.
   *
   * @since 3.0.0
   */
  private instant(key: string): string {
    return this.translateService.instant(key);
  }
}
